<template>
  <div>
    <section>
      <template>
        <v-container fluid>
          <v-row>
            <!-- Main Content Area -->
            <v-col
              :cols="sidebarDetails && $vuetify.breakpoint.lgAndUp ? 9 : 12"
              class="main-content-area"
            >
              <!-- Last Calculation Info -->
              <v-row v-if="lastCalculationDate">
                <v-col cols="12">
                  <v-alert dense elevation="2" class="mb-0">
                    <div class="d-flex align-center">
                      <v-icon small left>mdi-clock-outline</v-icon>
                      <span class="text-caption">
                        <strong>Letzte Aktualisierung:</strong>
                        {{ formatCalculationDate(lastCalculationDate) }}
                      </span>
                    </div>
                  </v-alert>
                </v-col>
              </v-row>

              <!-- Charts Row -->
              <v-row class="match-height">
                <!-- Traffic Light Distribution Donut Chart -->
                <v-col cols="12" md="3" lg="3">
                  <v-card height="400">
                    <traffic-light-distribution-chart
                      :traffic-light-stats="trafficLightStats"
                      :total-count="performanceTargetsWithResults.length"
                      :performance-data="performanceTargetsWithResults"
                      :loading="helpers.initialLoading"
                      :height="300"
                      @status-selected="setSidebarDetails"
                    />
                  </v-card>
                </v-col>

                <!-- Trend Chart -->
                <v-col cols="12" md="9" lg="9">
                  <v-card height="400">
                    <trend-chart
                      :summary-data="performanceTargetResultsSummary"
                      :loading="helpers.initialLoading"
                      :height="280"
                    />
                  </v-card>
                </v-col>

                <!-- Performance by Group -->
                <v-col cols="12" md="12" lg="12">
                  <v-card height="400">
                    <group-performance-chart
                      :performance-data="performanceTargetsWithResults"
                      :targets="performanceTargets"
                      :loading="helpers.initialLoading"
                      :height="280"
                      @segment-selected="setSidebarDetails"
                    />
                  </v-card>
                </v-col>

                <!-- Performance Targets Completion Rate -->
                <v-col cols="12">
                  <v-card height="400">
                    <targets-completion-chart
                      :targets="performanceTargets"
                      :performance-data="performanceTargetsWithResults"
                      :loading="helpers.initialLoading"
                      :height="300"
                      @target-selected="setSidebarDetails"
                    />
                  </v-card>
                </v-col>
              </v-row>

              <!-- Member Performance Radar Chart and Scatter Chart -->
              <v-row class="mt-4">
                <!-- Performance Distribution Scatter Chart -->
                <v-col cols="12">
                  <v-card height="600">
                    <performance-scatter-chart
                      :targets="performanceTargets"
                      :performance-data="performanceTargetsWithResults"
                      :loading="helpers.initialLoading"
                      :height="500"
                      @point-selected="setSidebarDetails"
                    />
                  </v-card>
                </v-col>
              </v-row>

              <!-- Top/Bottom Performers and Monthly Trend -->
              <!-- <v-row class="mt-4"> -->
              <!-- Top/Bottom Performers -->
              <!-- <v-col cols="12" md="6" lg="4">
                  <v-card height="500">
                    <top-bottom-performers-card
                      :targets="performanceTargets"
                      :performance-data="performanceTargetsWithResults"
                      :loading="helpers.initialLoading"
                      :content-height="400"
                      @performer-selected="setSidebarDetails"
                    />
                  </v-card>
                </v-col>
                </v-row> -->
            </v-col>

            <!-- Sidebar (Desktop Only) -->
            <v-col
              v-if="sidebarDetails && $vuetify.breakpoint.lgAndUp"
              cols="3"
              class="sidebar-column"
            >
              <chart-details-sidebar
                :details="sidebarDetails"
                :sidebar-height="'calc(100vh - 180px)'"
                @close="clearSidebarSelection"
              />
            </v-col>
          </v-row>

          <!-- Bottom Sheet (Mobile Only) -->
          <chart-details-bottom-sheet
            v-if="$vuetify.breakpoint.mdAndDown && sidebarDetails"
            v-model="showMobileSheet"
            :details="sidebarDetails"
            @close="clearSidebarSelection"
          />
        </v-container>
      </template>
    </section>
    <section v-if="checkIfSupport">
      <support-tools :sources="code"></support-tools>
    </section>
  </div>
</template>

<script>
import { ADMIN, MEMBER_MANAGEMENT } from "@/store/modules.js";
import {
  GET_USER_LIST,
  GET_PERFORMANCE_TARGETS,
  GET_PERFORMANCE_TARGET_RESULTS,
  GET_PERFORMANCE_TARGET_RESULTS_SUMMARY,
} from "@/store/action-types.js";
import SupportTools from "@/components/_system/helpers/SupportTools.vue";
import TrafficLightDistributionChart from "@/components/member-management/performance-targets/TrafficLightDistributionChart.vue";
import TrendChart from "@/components/member-management/performance-targets/TrendChart.vue";
import GroupPerformanceChart from "@/components/member-management/performance-targets/GroupPerformanceChart.vue";
import TargetsCompletionChart from "@/components/member-management/performance-targets/TargetsCompletionChart.vue";
// import TopBottomPerformersCard from "@/components/member-management/performance-targets/TopBottomPerformersCard.vue";
import PerformanceScatterChart from "@/components/member-management/performance-targets/PerformanceScatterChart.vue";
import ChartDetailsSidebar from "@/components/member-management/performance-targets/ChartDetailsSidebar.vue";
import ChartDetailsBottomSheet from "@/components/member-management/performance-targets/ChartDetailsBottomSheet.vue";

export default {
  name: "member-management-performance-targets-analysis",
  components: {
    SupportTools,
    TrafficLightDistributionChart,
    TrendChart,
    GroupPerformanceChart,
    TargetsCompletionChart,
    // TopBottomPerformersCard,
    PerformanceScatterChart,
    ChartDetailsSidebar,
    ChartDetailsBottomSheet,
  },
  data() {
    return {
      helpers: {
        initialLoading: true,
      },
      sidebarDetails: null,
      showMobileSheet: false,
    };
  },
  computed: {
    performanceTargets() {
      return this.$store.state.memberManagement.performanceTargets;
    },
    performanceTargetResults() {
      return this.$store.state.memberManagement.performanceTargetResults;
    },
    performanceTargetResultsSummary() {
      return this.$store.state.memberManagement.performanceTargetResultsSummary;
    },
    performanceTargetsWithResults() {
      return this.memberships.map((membership) => {
        const result = this.performanceTargetResults.find(
          (result) => result.user.uid === membership.user.uid
        );

        const performanceTargetResults = {};
        if (result) {
          result.performanceTargets.forEach((targetResult) => {
            performanceTargetResults[targetResult.performanceTarget.id] = {
              ...targetResult,
            };
          });
        }

        return {
          ...membership,
          totalPerformanceTargetResultStatus: result
            ? result.totalStatus
            : "unknown",
          trafficLightStatus: result ? result.trafficLightStatus : "unknown",
          performanceTargetResults,
          groupByGroup:
            membership.group.sortKey.toString().padStart(4, "0") +
            "_" +
            membership.group.title,
          groupByStatus: result ? result.totalStatus : "unknown",
          groupByTrafficLight: result ? result.trafficLightStatus : "unknown",
        };
      });
    },
    memberships() {
      return this.$store.state.admin.membershipsPublic;
    },

    // Traffic Light Statistics
    trafficLightStats() {
      const total = this.performanceTargetsWithResults.length || 1;
      const green = this.performanceTargetsWithResults.filter(
        (r) => r.trafficLightStatus === "green"
      ).length;
      const yellow = this.performanceTargetsWithResults.filter(
        (r) => r.trafficLightStatus === "yellow"
      ).length;
      const red = this.performanceTargetsWithResults.filter(
        (r) => r.trafficLightStatus === "red"
      ).length;

      return {
        green,
        yellow,
        red,
        greenPercent: Math.round((green / total) * 100),
        yellowPercent: Math.round((yellow / total) * 100),
        redPercent: Math.round((red / total) * 100),
      };
    },

    // Get the most recent calculation date from all results
    lastCalculationDate() {
      if (
        !this.performanceTargetResults ||
        this.performanceTargetResults.length === 0
      ) {
        return null;
      }

      const dates = this.performanceTargetResults
        .filter((r) => r.meta && r.meta.lastCalculatedAt)
        .map((r) => r.meta.lastCalculatedAt.toDate());

      if (dates.length === 0) return null;

      return new Date(Math.max(...dates));
    },

    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    code() {
      return [
        {
          title: "performanceTargetsWithResults",
          data: this.performanceTargetsWithResults,
        },
        { title: "memberships", data: this.memberships },
      ];
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      // Get today's date
      // const today = new Date();

      // Calculate date 20 days ago for summary query
      const twentyDaysAgo = new Date();
      twentyDaysAgo.setDate(twentyDaysAgo.getDate() - 20);
      const startDate = twentyDaysAgo.toISOString().split("T")[0];
      const endDate = new Date().toISOString().split("T")[0];

      await this.$store.dispatch(`${ADMIN}${GET_USER_LIST}`, {
        organizationId: this.$route.params.organizationId,
        queryIsFiltered: true,
        query: {
          type: "active",
        },
      });

      await this.$store.dispatch(
        `${MEMBER_MANAGEMENT}${GET_PERFORMANCE_TARGETS}`,
        {
          organizationId: this.$route.params.organizationId,
        }
      );

      await this.$store.dispatch(
        `${MEMBER_MANAGEMENT}${GET_PERFORMANCE_TARGET_RESULTS}`,
        {
          organizationId: this.$route.params.organizationId,
        }
      );

      await this.$store.dispatch(
        `${MEMBER_MANAGEMENT}${GET_PERFORMANCE_TARGET_RESULTS_SUMMARY}`,
        {
          organizationId: this.$route.params.organizationId,
          startDate: startDate,
          endDate: endDate,
          limit: 20,
        }
      );

      setTimeout(() => {
        this.helpers.initialLoading = false;
      }, 1);
    },
    setSidebarDetails(details) {
      this.sidebarDetails = details;
      // Open mobile sheet when details are set
      if (this.$vuetify.breakpoint.mdAndDown) {
        this.showMobileSheet = true;
      }
    },
    clearSidebarSelection() {
      this.sidebarDetails = null;
      this.showMobileSheet = false;
    },
    formatCalculationDate(date) {
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Format the date and time
      const dateStr = date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Add relative time
      let relativeTime = "";
      if (diffMins < 1) {
        relativeTime = "gerade eben";
      } else if (diffMins < 60) {
        relativeTime = `vor ${diffMins} Minute${diffMins !== 1 ? "n" : ""}`;
      } else if (diffHours < 24) {
        relativeTime = `vor ${diffHours} Stunde${diffHours !== 1 ? "n" : ""}`;
      } else if (diffDays === 1) {
        relativeTime = "gestern";
      } else if (diffDays < 7) {
        relativeTime = `vor ${diffDays} Tagen`;
      }

      return relativeTime
        ? `${dateStr} um ${timeStr} Uhr (${relativeTime})`
        : `${dateStr} um ${timeStr} Uhr`;
    },
  },
};
</script>

<style scoped>
.sidebar-column {
  position: sticky;
  top: 80px;
  align-self: flex-start;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}
</style>
